import React, { useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Stack,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import { Search, UserPlus, X, Pencil, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useToast } from '../layout/Layout';
import { createUser, deleteUser, listUsers, updateUser, User } from '../services/settingsServices/usersService';
import { authProvider } from '../utils/authProvider';
import { useUserPermissions } from '../context/UserPermissionsContext';
import './Users.css';

const getInitials = (first: string, last: string) =>
    `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();

const AVATAR_CLASSES = ['av-j', 'av-s', 'av-a', 'av-d'];

const avatarClassFor = (userId: string): string => {
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
        hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
    }
    return AVATAR_CLASSES[hash % AVATAR_CLASSES.length];
};

interface UserForm {
    userId: string;
    email: string;
    firstName: string;
    lastName: string;
    active: boolean;
    admin: boolean;
}

const EMPTY_FORM: UserForm = { userId: '', email: '', firstName: '', lastName: '', active: true, admin: false };

const VALIDATION = {
    userId: {
        regex: /^[a-zA-Z0-9._-]+$/,
        message: 'Please enter a valid User ID',
    },
    email: {
        regex: /^[a-zA-Z0-9._%+-]+@(albertsons\.com|safeway\.com)$/i,
        message: 'Email must be an @albertsons.com or @safeway.com address',
    },
    name: {
        regex: /^[A-Za-z\s\-']+$/,
        message: 'Please enter a valid user name',
    },
};

const Users = () => {
    const toast = useToast();
    const { isAdmin } = useUserPermissions();
    const [users, setUsers] = useState<User[]>([]);
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState<UserForm>(EMPTY_FORM);
    const [errors, setErrors] = useState<Partial<Record<keyof UserForm, string>>>({});
    const [editingUserId, setEditingUserId] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [pageSize, setPageSize] = useState(25);
    const [page, setPage] = useState(1);
    const [confirmDeleteUser, setConfirmDeleteUser] = useState<User | null>(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        listUsers()
            .then(setUsers)
            .catch((err: any) => {
                const data = err?.response?.data;
                const message =
                    data?.message ||
                    data?.error ||
                    (typeof data === 'string' ? data : null) ||
                    err?.message ||
                    'Failed to load users.';
                toast.showError(message);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return users;
        return users.filter(
            (u) =>
                `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
                u.email.toLowerCase().includes(q) ||
                u.userId.toLowerCase().includes(q)
        );
    }, [users, query]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(page, totalPages);
    const paginated = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

    useEffect(() => { setPage(1); }, [query, pageSize]);

    const handleOpen = () => {
        setEditingUserId(null);
        setForm(EMPTY_FORM);
        setErrors({});
        setOpen(true);
    };

    const [originalForm, setOriginalForm] = useState<UserForm>(EMPTY_FORM);

    const handleEdit = (user: User) => {
        const initial: UserForm = {
            userId: user.userId,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            active: user.active,
            admin: user.admin,
        };
        setEditingUserId(user.userId);
        setForm(initial);
        setOriginalForm(initial);
        setErrors({});
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const handleChange = (field: keyof UserForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
        setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const handleBlur = (field: keyof UserForm) => () => {
        const value = (form[field] as string) ?? '';
        setErrors((prev) => {
            const next = { ...prev };
            if (field === 'userId' && !editingUserId) {
                if (!value.trim()) next.userId = 'User ID is required';
                else if (!VALIDATION.userId.regex.test(value.trim())) next.userId = VALIDATION.userId.message;
                else if (users.some((u) => u.userId.toLowerCase() === value.trim().toLowerCase() && u.userId !== editingUserId)) next.userId = 'A user with this ID already exists';
                else delete next.userId;
            }
            if (field === 'email') {
                if (!value.trim()) next.email = 'Email ID is required';
                else if (!VALIDATION.email.regex.test(value.trim())) next.email = VALIDATION.email.message;
                else delete next.email;
            }
            if (field === 'firstName') {
                if (!value.trim()) next.firstName = 'First name is required';
                else if (!VALIDATION.name.regex.test(value.trim())) next.firstName = `First name: ${VALIDATION.name.message}`;
                else delete next.firstName;
            }
            if (field === 'lastName') {
                if (!value.trim()) next.lastName = 'Last name is required';
                else if (!VALIDATION.name.regex.test(value.trim())) next.lastName = `Last name: ${VALIDATION.name.message}`;
                else delete next.lastName;
            }
            return next;
        });
    };

    const handleToggle = (field: 'active' | 'admin') => (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm((prev) => ({ ...prev, [field]: e.target.checked }));
    };

    const validate = (): boolean => {
        const next: Partial<Record<keyof UserForm, string>> = {};
        if (!form.userId.trim()) {
            next.userId = 'User ID is required';
        } else if (!VALIDATION.userId.regex.test(form.userId.trim())) {
            next.userId = VALIDATION.userId.message;
        }
        if (!form.email.trim()) {
            next.email = 'Email ID is required';
        } else if (!VALIDATION.email.regex.test(form.email.trim())) {
            next.email = VALIDATION.email.message;
        }
        if (!form.firstName.trim()) {
            next.firstName = 'First name is required';
        } else if (!VALIDATION.name.regex.test(form.firstName.trim())) {
            next.firstName = `First name: ${VALIDATION.name.message}`;
        }
        if (!form.lastName.trim()) {
            next.lastName = 'Last name is required';
        } else if (!VALIDATION.name.regex.test(form.lastName.trim())) {
            next.lastName = `Last name: ${VALIDATION.name.message}`;
        }

        const duplicate = users.some(
            (u) =>
                u.userId.toLowerCase() === form.userId.trim().toLowerCase() &&
                u.userId !== editingUserId
        );
        if (duplicate) {
            next.userId = 'A user with this ID already exists';
        }

        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const isFormValid = useMemo(() => {
        const idOk = editingUserId
            ? true
            : form.userId.trim() !== '' && VALIDATION.userId.regex.test(form.userId.trim());
        const emailOk = form.email.trim() !== '' && VALIDATION.email.regex.test(form.email.trim());
        const firstOk = form.firstName.trim() !== '' && VALIDATION.name.regex.test(form.firstName.trim());
        const lastOk = form.lastName.trim() !== '' && VALIDATION.name.regex.test(form.lastName.trim());
        return idOk && emailOk && firstOk && lastOk;
    }, [form, editingUserId]);

    const isDirty = useMemo(() => {
        if (!editingUserId) return true;
        return (
            form.email.trim() !== originalForm.email.trim() ||
            form.firstName.trim() !== originalForm.firstName.trim() ||
            form.lastName.trim() !== originalForm.lastName.trim() ||
            form.active !== originalForm.active ||
            form.admin !== originalForm.admin
        );
    }, [form, editingUserId, originalForm]);

    const handleSubmit = async () => {
        if (!validate()) return;
        const submitted: User = {
            userId: form.userId.trim(),
            email: form.email.trim(),
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            active: form.active,
            admin: form.admin,
        };

        setSubmitting(true);
        try {
            const { userId: actorUserId } = authProvider();
            if (editingUserId) {
                await updateUser(editingUserId, submitted, actorUserId);
                setUsers((prev) => prev.map((u) => (u.userId === editingUserId ? submitted : u)));
                toast.showSuccess(`User "${submitted.firstName} ${submitted.lastName}" updated successfully!`);
            } else {
                await createUser(submitted, actorUserId);
                setUsers((prev) => [submitted, ...prev]);
                toast.showSuccess(`User "${submitted.firstName} ${submitted.lastName}" added successfully!`);
            }
            setOpen(false);
        } catch (err: any) {
            const data = err?.response?.data;
            const message =
                data?.message ||
                data?.error ||
                (typeof data === 'string' ? data : null) ||
                err?.message ||
                (editingUserId ? 'Failed to update user. Please try again.' : 'Failed to add user. Please try again.');
            toast.showError(message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!confirmDeleteUser) return;
        setDeleting(true);
        try {
            await deleteUser(confirmDeleteUser.userId);
            setUsers((prev) => prev.filter((u) => u.userId !== confirmDeleteUser.userId));
            toast.showSuccess(`User "${confirmDeleteUser.firstName} ${confirmDeleteUser.lastName}" deleted successfully.`);
            setConfirmDeleteUser(null);
        } catch (err: any) {
            const data = err?.response?.data;
            const message =
                data?.message ||
                data?.error ||
                (typeof data === 'string' ? data : null) ||
                err?.message ||
                'Failed to delete user. Please try again.';
            toast.showError(message);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="users-page">
            <div className="settings-title-row">
                <p className="settings-title">Users</p>
                <span className="users-count">{users.length} users</span>
            </div>
            <div className="users-card">
                <div className="users-toolbar">
                    <div className="users-search">
                        <Search size={16} className="users-search-icon" />
                        <input
                            type="text"
                            placeholder="Search by name or email…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                        {query && (
                            <button className="users-search-clear" onClick={() => setQuery('')} title="Clear">
                                <X size={14} />
                            </button>
                        )}
                    </div>
                    {isAdmin && (
                        <button className="users-add-btn" onClick={handleOpen}>
                            <UserPlus size={16} />
                            Add User
                        </button>
                    )}
                </div>

                <div className="users-table-scroll">
                    <table className="users-table">
                        <thead>
                            <tr>
                                <th className="user-col">Name</th>
                                <th>User ID</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Status</th>
                                {isAdmin && <th className="action-col">Action</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {paginated.length === 0 ? (
                                <tr>
                                    <td colSpan={isAdmin ? 6 : 5} className="users-empty">
                                        {query ? `No users match "${query}".` : 'No users found.'}
                                    </td>
                                </tr>
                            ) : (
                                paginated.map((u) => (
                                    <tr key={u.userId}>
                                        <td className="user-cell">
                                            <div className="user-cell-inner">
                                                <span className={`avatar ${avatarClassFor(u.userId)}`}>
                                                    {getInitials(u.firstName, u.lastName)}
                                                </span>
                                                {u.firstName} {u.lastName}
                                            </div>
                                        </td>
                                        <td className="mono">{u.userId}</td>
                                        <td>{u.email}</td>
                                        <td>
                                            <span className={`role-badge ${u.admin ? 'role-admin' : 'role-user'}`}>
                                                {u.admin ? 'Admin' : 'User'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${u.active ? 'status-active' : 'status-inactive'}`}>
                                                <span className="status-dot" />
                                                {u.active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        {isAdmin && (
                                            <td className="action-col">
                                                <div className="row-actions">
                                                    <button
                                                        type="button"
                                                        className="row-btn row-btn-edit"
                                                        onClick={() => handleEdit(u)}
                                                        title="Edit user"
                                                    >
                                                        <Pencil size={15} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="row-btn row-btn-delete"
                                                        onClick={() => setConfirmDeleteUser(u)}
                                                        title="Delete user"
                                                    >
                                                        <Trash2 size={15} />
                                                    </button>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="users-pagination">
                    <div className="users-page-size">
                        <span>Rows per page:</span>
                        {[25, 50, 100].map((n) => (
                            <button
                                key={n}
                                className={`page-size-btn${pageSize === n ? ' active' : ''}`}
                                onClick={() => setPageSize(n)}
                            >
                                {n}
                            </button>
                        ))}
                    </div>
                    <div className="users-page-nav">
                        <span className="page-info">
                            {filtered.length === 0
                                ? '0 users'
                                : `${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, filtered.length)} of ${filtered.length}`}
                        </span>
                        <button
                            className="page-nav-btn"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={safePage === 1}
                            title="Previous page"
                        >
                            <ChevronLeft size={15} />
                        </button>
                        {(() => {
                            const pages: (number | '...')[] = [];
                            if (totalPages <= 7) {
                                for (let i = 1; i <= totalPages; i++) pages.push(i);
                            } else {
                                pages.push(1);
                                if (safePage > 3) pages.push('...');
                                for (let i = Math.max(2, safePage - 1); i <= Math.min(totalPages - 1, safePage + 1); i++) pages.push(i);
                                if (safePage < totalPages - 2) pages.push('...');
                                pages.push(totalPages);
                            }
                            return pages.map((p, i) =>
                                p === '...' ? (
                                    <span key={`ellipsis-${i}`} className="page-ellipsis">…</span>
                                ) : (
                                    <button
                                        key={p}
                                        className={`page-nav-btn page-num-btn${safePage === p ? ' active' : ''}`}
                                        onClick={() => setPage(p as number)}
                                    >
                                        {p}
                                    </button>
                                )
                            );
                        })()}
                        <button
                            className="page-nav-btn"
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={safePage === totalPages}
                            title="Next page"
                        >
                            <ChevronRight size={15} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Add / Edit dialog */}
            <Dialog
                open={open}
                onClose={(_event, reason) => {
                    if (reason === 'backdropClick') return;
                    handleClose();
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 600 }}>{editingUserId ? 'Edit User' : 'Add User'}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2.5} sx={{ mt: 1 }}>
                        <TextField
                            label="User ID"
                            required
                            fullWidth
                            value={form.userId}
                            onChange={handleChange('userId')}
                            onBlur={handleBlur('userId')}
                            error={!!errors.userId}
                            helperText={editingUserId ? 'User ID cannot be changed' : errors.userId}
                            disabled={!!editingUserId}
                        />
                        <TextField
                            label="Email ID"
                            required
                            fullWidth
                            type="email"
                            value={form.email}
                            onChange={handleChange('email')}
                            onBlur={handleBlur('email')}
                            error={!!errors.email}
                            helperText={errors.email}
                        />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
                            <TextField
                                label="First Name"
                                required
                                fullWidth
                                value={form.firstName}
                                onChange={handleChange('firstName')}
                                onBlur={handleBlur('firstName')}
                                error={!!errors.firstName}
                                helperText={errors.firstName}
                            />
                            <TextField
                                label="Last Name"
                                required
                                fullWidth
                                value={form.lastName}
                                onChange={handleChange('lastName')}
                                onBlur={handleBlur('lastName')}
                                error={!!errors.lastName}
                                helperText={errors.lastName}
                            />
                        </Stack>
                        <Stack direction="row" spacing={3} sx={{ pt: 0.5 }}>
                            <FormControlLabel
                                control={<Checkbox checked={form.admin} onChange={handleToggle('admin')} />}
                                label="Admin"
                            />
                            <FormControlLabel
                                control={<Checkbox checked={form.active} onChange={handleToggle('active')} />}
                                label="Active"
                            />
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleClose} color="inherit" disabled={submitting}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={submitting || !isFormValid || !isDirty}>
                        {editingUserId ? 'Save Changes' : submitting ? 'Adding…' : 'Add User'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete confirmation dialog */}
            <Dialog
                open={!!confirmDeleteUser}
                onClose={(_event, reason) => {
                    if (reason === 'backdropClick') return;
                    setConfirmDeleteUser(null);
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 600 }}>Delete User</DialogTitle>
                <DialogContent>
                    <p style={{ margin: '8px 0 0', fontSize: 14, color: '#344054' }}>
                        Are you sure you want to delete{' '}
                        <strong>{confirmDeleteUser?.firstName} {confirmDeleteUser?.lastName}</strong>?
                        This action cannot be undone.
                    </p>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setConfirmDeleteUser(null)} color="inherit" disabled={deleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} variant="contained" color="error" disabled={deleting}>
                        {deleting ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Users;
